import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { Box, Button, Flex, Group, ScrollArea, Tabs } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';

import { useGetPatientQuery } from '@/store/slices/api/patientsApiSlice';
import { useDeviceAndLeadsRecallFeatureFlag } from '@/hooks/useDeviceAndLeadsRecallFeatureFlag';
import AccessRestricted from '@/components/AccessRestricted'; // Add this import
import { ActivityFeed } from '@/components/ActivityFeed';

import SwitchTabs from '@/ui/SwitchTabs';

import ClinicalNotes from './ClinicalNotes';
import Devices from './Devices';
import Documents from './Documents';
import OverviewSkeleton from './OverviewSkeleton';
import PatientDetails from './PatientDetails';
import PatientOverview from './PatientOverview';
import styles from './PatientOverviewPage.module.scss';
import PatientReportsList from './PatientsReportList';
import Recalls from './Recalls';
import ScheduleList from './ScheduleList';

enum PatientTabs {
  Reports = 'reports',
  Schedule = 'schedule',
  Notes = 'notes',
  Documents = 'documents',
  PatientOverview = 'patient-overview',
  Devices = 'devices',
  Recalls = 'recalls'
}

interface TabConfig {
  id: PatientTabs;
  label: string;
}

const leftContainerTabOptions: TabConfig[] = [
  { id: PatientTabs.PatientOverview, label: 'Patient Overview' },
  { id: PatientTabs.Devices, label: 'Devices' },
  { id: PatientTabs.Recalls, label: 'Recalls' }
];

const rightContainerTabOptions: TabConfig[] = [
  { id: PatientTabs.Reports, label: 'Reports' },
  { id: PatientTabs.Schedule, label: 'Schedule' },
  { id: PatientTabs.Notes, label: 'Notes' },
  { id: PatientTabs.Documents, label: 'Documents' }
];

const PatientOverviewPage = () => {
  const { uuid: patientId } = useParams() as { uuid: string };
  const {
    data: patient,
    isLoading,
    error
  } = useGetPatientQuery(patientId, {
    refetchOnMountOrArgChange: true
  });
  const { isRecallListPageEnabled } = useDeviceAndLeadsRecallFeatureFlag();

  const [activeRightTab, setActiveRightTab] = useState(PatientTabs.Reports);
  const [activeLeftTab, setActiveLeftTab] = useState<PatientTabs>(PatientTabs.PatientOverview);
  const [isDeviceModalOpen, setIsDeviceModalOpen] = useState(false);

  // Check for 401/403 access denied errors
  const isAccessDenied =
    error && 'status' in error && (error.status === 401 || error.status === 403);

  // Only show Recalls tab if feature flag is enabled
  const filteredLeftContainerTabOptions = isRecallListPageEnabled
    ? leftContainerTabOptions
    : leftContainerTabOptions.filter((tab) => tab.id !== PatientTabs.Recalls);

  const handleLeftTabChange = (value: string | null) => {
    if (value) {
      setActiveLeftTab(value as PatientTabs);
    }
  };

  const handleAddDeviceClick = () => {
    setIsDeviceModalOpen(true);
  };

  const renderTab = ({ id, label }: TabConfig) => (
    <Tabs.Tab key={id} value={id} className={styles.tab}>
      <Group gap={4}>{label}</Group>
    </Tabs.Tab>
  );

  const renderLeftContent = () => {
    switch (activeLeftTab) {
      case PatientTabs.PatientOverview:
        return <PatientOverview />;
      case PatientTabs.Devices:
        return (
          <Devices
            isDeviceModalOpen={isDeviceModalOpen}
            setIsDeviceModalOpen={setIsDeviceModalOpen}
          />
        );
      case PatientTabs.Recalls:
        return <Recalls patientId={patientId} />;
    }
  };

  const renderRightContent = () => {
    switch (activeRightTab) {
      case PatientTabs.Reports:
        return <PatientReportsList />;
      case PatientTabs.Schedule:
        return <ScheduleList />;
      case PatientTabs.Notes:
        return <ClinicalNotes />;
      case PatientTabs.Documents:
        return <Documents />;
    }
  };

  if (isAccessDenied) {
    return <AccessRestricted />;
  }

  if (isLoading || !patient) {
    return <OverviewSkeleton />;
  }

  return (
    <Flex direction={'column'} className={styles.container}>
      <PatientDetails patientId={patientId} />

      <Flex bg={'white'} flex={1}>
        <Flex flex={2} direction={'column'} className={styles.leftSection}>
          <Box py={11.5} px={24} className={styles.headerSectionContent}>
            <Flex justify="space-between" align="center">
              <Tabs
                variant="none"
                value={activeLeftTab}
                onChange={handleLeftTabChange}
                className={styles.tabs}>
                <Tabs.List className={styles.tabsList}>
                  {filteredLeftContainerTabOptions.map(renderTab)}
                </Tabs.List>
              </Tabs>
              {activeLeftTab === PatientTabs.Devices && (
                <Button
                  size="xs"
                  onClick={handleAddDeviceClick}
                  variant="subtle"
                  leftSection={<IconPlus size={16} />}>
                  Add Device
                </Button>
              )}
            </Flex>
          </Box>
          <ScrollArea.Autosize
            className={styles.scrollArea}
            type="always"
            scrollbarSize={5}
            scrollbars="y">
            <Box px={24} py={12} h={'100%'}>
              {renderLeftContent()}
            </Box>
          </ScrollArea.Autosize>
        </Flex>

        <Flex flex={1.15} direction={'column'} className={styles.rightSection}>
          <Box py={12} px={24} className={styles.headerSectionContent}>
            <SwitchTabs
              options={rightContainerTabOptions}
              value={activeRightTab}
              onChange={setActiveRightTab}
            />
          </Box>
          <ScrollArea.Autosize
            className={styles.scrollArea}
            type="always"
            scrollbarSize={5}
            scrollbars="y">
            <Box px={24} py={12} h={'100%'}>
              {renderRightContent()}
            </Box>
          </ScrollArea.Autosize>
        </Flex>
      </Flex>

      <ActivityFeed patientId={patientId} isPatientDataLoading={isLoading} />
    </Flex>
  );
};

export default PatientOverviewPage;
